import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, Loader2, Sparkles, User, ChevronDown, ChevronUp, ArrowRight, Mic, MicOff, Clock, Heart } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { useWebSpeechRecording } from '@/hooks/useWebSpeechRecording';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface CrisisOverrideResponse {
  crisis?: boolean;
  intent?: string;
  redirectTo?: string;
  blockRecommendations?: boolean;
  message?: string;
}

interface WellnessConciergeProps {
  onStageRecommended?: (stage: number) => void;
}

const CRISIS_HELP_URL = 'https://resonow.org/crisis-resources';

const STAGE_NAMES: Record<number, { en: string; zh: string }> = {
  0: { en: 'Crisis Intervention', zh: '危机干预' },
  1: { en: 'Harmonize Mind & Body', zh: '身心调和' },
  2: { en: 'Analysis & Investigation', zh: '分析与探究' },
  3: { en: 'Transformation & Growth', zh: '转化与成长' },
  4: { en: 'Integration & Mastery', zh: '整合与精通' },
};

// ── Concern category chips ──
const CONCERN_CHIPS_EN = [
  { label: '😟 Anxiety / Stress', value: 'anxiety and stress' },
  { label: '😴 Sleep Issues', value: 'sleep problems' },
  { label: '😔 Low Mood', value: 'feeling down and low mood' },
  { label: '💔 Relationship', value: 'relationship difficulties' },
  { label: '🔥 Burnout', value: 'burnout and exhaustion' },
  { label: '🌱 Personal Growth', value: 'personal growth and self-discovery' },
  { label: '🧘 Spiritual', value: 'spiritual exploration and inner peace' },
  { label: '💪 Body & Health', value: 'physical health and body issues' },
];

const CONCERN_CHIPS_ZH = [
  { label: '😟 焦虑 / 压力', value: '焦虑和压力' },
  { label: '😴 睡眠问题', value: '睡眠问题' },
  { label: '😔 情绪低落', value: '情绪低落和抑郁感' },
  { label: '💔 关系困扰', value: '人际关系困扰' },
  { label: '🔥 疲倦倦怠', value: '倦怠和疲惫' },
  { label: '🌱 个人成长', value: '个人成长和自我探索' },
  { label: '🧘 灵性探索', value: '灵性探索和内在平静' },
  { label: '💪 身体健康', value: '身体健康和身体问题' },
];

// ── Duration chips ──
const DURATION_CHIPS_EN = [
  { label: 'Just now', value: 'just started recently' },
  { label: 'Weeks', value: 'for several weeks' },
  { label: 'Months', value: 'for months' },
  { label: 'Long-term', value: 'for a long time, possibly years' },
];

const DURATION_CHIPS_ZH = [
  { label: '刚开始', value: '最近刚开始' },
  { label: '几周了', value: '持续了几周' },
  { label: '几个月', value: '已经几个月了' },
  { label: '长期', value: '长期存在，可能好几年了' },
];

const FOLLOWUP_SUGGESTIONS_EN = [
  'Tell me more about this stage',
  'What therapies do you recommend?',
  'How long does healing usually take?',
  'Can I combine multiple modalities?',
];

const FOLLOWUP_SUGGESTIONS_ZH = [
  '能详细介绍一下这个阶段吗？',
  '你推荐哪些具体疗法？',
  '疗愈通常需要多长时间？',
  '可以同时体验多种疗法吗？',
];

/**
 * Parse [quick_reply: opt1 | opt2 | opt3] from AI response.
 * Returns { cleanContent, quickReplies } where cleanContent has the tag stripped.
 */
function parseQuickReplies(content: string): { cleanContent: string; quickReplies: string[] } {
  const match = content.match(/\[quick_reply:\s*(.+?)\]/);
  if (!match) return { cleanContent: content, quickReplies: [] };
  const options = match[1].split('|').map(o => o.trim()).filter(Boolean);
  const cleanContent = content.replace(/\[quick_reply:\s*.+?\]/g, '').trim();
  return { cleanContent, quickReplies: options };
}

/**
 * Build a structured prompt from guided selections + optional free text.
 */
function buildStructuredInput(
  concern: string | null,
  duration: string | null,
  freeText: string,
  isZh: boolean,
): string {
  const parts: string[] = [];

  if (concern) {
    parts.push(isZh ? `我的主要困扰是：${concern}` : `My main concern: ${concern}`);
  }
  if (duration) {
    parts.push(isZh ? `持续时间：${duration}` : `Duration: ${duration}`);
  }
  if (freeText.trim()) {
    parts.push(isZh ? `补充描述：${freeText.trim()}` : `Additional details: ${freeText.trim()}`);
  }

  if (!concern && !duration) return freeText.trim();

  return parts.join('\n');
}

const WellnessConcierge: React.FC<WellnessConciergeProps> = ({ onStageRecommended }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  
  const isZh = i18n.language?.startsWith('zh');
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [detectedStage, setDetectedStage] = useState<number | null>(null);
  const [isCrisisState, setIsCrisisState] = useState(false);
  const [quickReplyOptions, setQuickReplyOptions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Guided input state
  const [selectedConcern, setSelectedConcern] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  const [guidedStep, setGuidedStep] = useState<'concern' | 'duration' | 'details'>('concern');

  const redirectToCrisisResources = useCallback((url?: string) => {
    const targetUrl = url && /^https?:\/\//i.test(url) ? url : CRISIS_HELP_URL;
    window.location.assign(targetUrl);
  }, []);

  const {
    isRecording,
    transcript,
    interimTranscript,
    isSupported: speechSupported,
    startRecording,
    stopRecording,
    resetTranscript,
  } = useWebSpeechRecording({ language: '', continuous: false, interimResults: true });

  useEffect(() => {
    if (transcript) {
      setInputValue(prev => prev + transcript);
      resetTranscript();
    }
  }, [transcript, resetTranscript]);

  const concernChips = isZh ? CONCERN_CHIPS_ZH : CONCERN_CHIPS_EN;
  const durationChips = isZh ? DURATION_CHIPS_ZH : DURATION_CHIPS_EN;
  const followUpSuggestions = isZh ? FOLLOWUP_SUGGESTIONS_ZH : FOLLOWUP_SUGGESTIONS_EN;

  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStageClick = useCallback((stageNum: number) => {
    if (onStageRecommended) {
      onStageRecommended(stageNum);
    }
  }, [onStageRecommended]);

  const handleMicToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  const markdownComponents = useMemo(() => ({
    strong: ({ children, ...props }: any) => {
      const text = String(children);
      const stageMatch = text.match(/Recommended\s*Stage:\s*Stage\s*(\d)/i) 
        || text.match(/推荐阶段[：:]\s*Stage\s*(\d)/i)
        || text.match(/推荐阶段[：:]\s*第(\d)阶段/i);
      
      if (stageMatch) {
        const stageNum = parseInt(stageMatch[1], 10);
        if (stageNum >= 0 && stageNum <= 4) {
          const stageName = isZh ? STAGE_NAMES[stageNum]?.zh : STAGE_NAMES[stageNum]?.en;
          return (
            <button
              onClick={() => handleStageClick(stageNum)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 my-1 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 transition-colors cursor-pointer border-0"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {isZh ? `进入 Stage ${stageNum} — ${stageName}` : `Go to Stage ${stageNum} — ${stageName}`}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          );
        }
      }
      return <strong {...props}>{children}</strong>;
    },
    a: ({ href, children, ...props }: any) => {
      if (href && href.startsWith('/book-an-appointment/')) {
        return (
          <button
            onClick={() => navigate(href)}
            className="inline-flex items-center gap-1 text-brand-600 font-medium hover:text-brand-700 underline underline-offset-2 cursor-pointer border-0 bg-transparent p-0"
          >
            {children}
            <ArrowRight className="h-3 w-3" />
          </button>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
    },
  }), [isZh, handleStageClick, navigate]);

  const sendMessage = useCallback(async (input: string) => {
    if (!input.trim() || isLoading) return;
    if (isCrisisState) {
      redirectToCrisisResources();
      return;
    }

    // Clear quick replies when user sends a new message
    setQuickReplyOptions([]);

    const userMsg: ChatMessage = { role: 'user', content: input };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setIsLoading(true);
    setIsExpanded(true);
    setDetectedStage(null);

    abortRef.current = new AbortController();
    let assistantContent = '';

    try {
      const messagesForApi = newMessages.map(m => ({ role: m.role, content: m.content }));

      const resp = await fetch(`${SUPABASE_URL}/functions/v1/wellness-concierge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ messages: messagesForApi }),
        signal: abortRef.current.signal,
      });

      if (!resp.ok) {
        if (resp.status === 429) throw new Error(isZh ? '请求过于频繁，请稍后再试' : 'Too many requests, please try again later.');
        if (resp.status === 402) throw new Error(isZh ? '消息额度已用完' : 'Message quota reached.');
        throw new Error(`Error ${resp.status}`);
      }

      const contentType = resp.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const jsonData: CrisisOverrideResponse = await resp.json();
        if (jsonData.crisis) {
          setIsCrisisState(true);
          setDetectedStage(null);
          setInputValue('');
          sessionStorage.setItem('crisis_override_active', 'true');
          setMessages([{ role: 'assistant', content: jsonData.message || (isZh ? '请立即寻求帮助。' : 'Please get immediate help now.') }]);
          setIsLoading(false);
          redirectToCrisisResources(jsonData.redirectTo);
          return;
        }
        setIsCrisisState(false);
      }

      if (!resp.body) throw new Error('No response body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';

      const processLine = (line: string) => {
        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line.startsWith(':') || line.trim() === '') return;
        if (!line.startsWith('data: ')) return;
        const jsonStr = line.slice(6).trim();
        if (jsonStr === '[DONE]') return;
        try {
          const parsed = JSON.parse(jsonStr);
          if (parsed.metadata) return;
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            assistantContent += content;
            // Strip quick_reply tags for display, but track them
            const { cleanContent } = parseQuickReplies(assistantContent);
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: cleanContent } : m);
              }
              return [...prev, { role: 'assistant', content: cleanContent }];
            });
          }
        } catch {
          // skip malformed SSE
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });
        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          const line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);
          processLine(line);
        }
      }
      if (textBuffer.trim()) {
        for (const raw of textBuffer.split('\n')) {
          if (raw) processLine(raw);
        }
      }

      // After streaming is complete, extract quick replies from full content
      const { cleanContent, quickReplies } = parseQuickReplies(assistantContent);
      if (quickReplies.length > 0) {
        setQuickReplyOptions(quickReplies);
        // Update final message to clean version
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant') {
            return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: cleanContent } : m);
          }
          return prev;
        });
      }

      if (!isCrisisState) {
        const stageMatch = assistantContent.match(/Stage\s*(\d)/i);
        if (stageMatch) {
          const stageNum = parseInt(stageMatch[1], 10);
          if (stageNum >= 0 && stageNum <= 4) {
            setDetectedStage(stageNum);
          }
        }
      }

    } catch (e: any) {
      if (e.name === 'AbortError') return;
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `⚠️ ${e.message || 'Something went wrong.'}` },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, isZh, isCrisisState, redirectToCrisisResources]);

  // Handle sending with structured input enrichment
  const handleSend = useCallback(() => {
    const structured = buildStructuredInput(selectedConcern, selectedDuration, inputValue, !!isZh);
    if (!structured.trim()) return;
    sendMessage(structured);
    setInputValue('');
    setSelectedConcern(null);
    setSelectedDuration(null);
    setGuidedStep('concern');
  }, [inputValue, selectedConcern, selectedDuration, isZh, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleConcernSelect = useCallback((value: string) => {
    if (selectedConcern === value) {
      setSelectedConcern(null);
      setGuidedStep('concern');
    } else {
      setSelectedConcern(value);
      setGuidedStep('duration');
    }
  }, [selectedConcern]);

  const handleDurationSelect = useCallback((value: string) => {
    if (selectedDuration === value) {
      setSelectedDuration(null);
    } else {
      setSelectedDuration(value);
      setGuidedStep('details');
    }
  }, [selectedDuration]);

  const handleQuickSend = useCallback(() => {
    if (!selectedConcern) return;
    const structured = buildStructuredInput(selectedConcern, selectedDuration, inputValue, !!isZh);
    sendMessage(structured);
    setInputValue('');
    setSelectedConcern(null);
    setSelectedDuration(null);
    setGuidedStep('concern');
  }, [selectedConcern, selectedDuration, inputValue, isZh, sendMessage]);

  // Handle quick reply button click
  const handleQuickReply = useCallback((reply: string) => {
    setQuickReplyOptions([]);
    sendMessage(reply);
  }, [sendMessage]);

  if (isCrisisState) {
    return (
      <div className="max-w-2xl mx-auto mb-10">
        <div className="rounded-xl border border-warm-200 bg-white shadow-sm overflow-hidden">
          <div className="p-6 text-center space-y-4">
            <h2 className="text-xl md:text-2xl font-serif text-foreground">
              {isZh ? '请立即寻求帮助' : 'Please Get Immediate Help'}
            </h2>
            <Button asChild size="lg" className="bg-brand-500 hover:bg-brand-600 text-white rounded-full">
              <a href={CRISIS_HELP_URL} target="_blank" rel="noopener noreferrer">
                {isZh ? '获取即时帮助' : 'Get Immediate Help'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const hasGuidedSelection = selectedConcern || selectedDuration;
  const canSendGuided = !!selectedConcern;
  const showGuidedInput = messages.length === 0 && !isCrisisState;

  return (
    <div className="max-w-2xl mx-auto mb-10">
      <div className="text-center mb-4">
        <h2 className="text-2xl md:text-3xl font-serif text-foreground">
          {isZh ? '你的' : 'Your Personal '}
          <span className="text-brand-500 italic">{isZh ? '专属疗愈顾问' : 'Wellness Concierge'}</span>
        </h2>
      </div>

      <div className="rounded-xl border border-warm-200 bg-white shadow-sm overflow-hidden">
        {/* Chat messages area */}
        {isExpanded && messages.length > 0 && (
          <ScrollArea ref={scrollAreaRef} className="h-[400px]">
            <div className="divide-y divide-warm-100">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'flex gap-3 px-4 py-3',
                    msg.role === 'user' ? 'flex-row-reverse' : '',
                    msg.role === 'assistant' ? 'bg-warm-50/50' : ''
                  )}
                >
                  <div className={cn(
                    'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5',
                    msg.role === 'user' ? 'bg-brand-100 text-brand-700' : 'bg-brand-50 text-brand-500'
                  )}>
                    {msg.role === 'user' ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                  </div>
                  <div className={cn(
                    'min-w-0 overflow-hidden max-w-[85%]',
                    msg.role === 'user' ? 'flex flex-col items-end' : ''
                  )}>
                    {msg.role === 'user' ? (
                      <div className="bg-brand-100 rounded-2xl rounded-tr-sm px-4 py-2.5">
                        <p className="text-sm text-foreground whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ) : (
                      <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-headings:my-2 prose-strong:text-brand-700">
                        <ReactMarkdown components={markdownComponents}>{msg.content}</ReactMarkdown>
                        {isLoading && idx === messages.length - 1 && (
                          <span className="inline-block w-1.5 h-4 bg-brand-400 animate-pulse ml-0.5 align-text-bottom" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Quick reply buttons — shown after AI asks a follow-up question */}
              {!isLoading && quickReplyOptions.length > 0 && (
                <div className="px-4 py-3 bg-brand-50/20">
                  <div className="flex flex-wrap gap-1.5">
                    {quickReplyOptions.map((option) => (
                      <button
                        key={option}
                        onClick={() => handleQuickReply(option)}
                        className="px-3 py-1.5 text-xs rounded-full border border-brand-300 bg-white text-brand-700 hover:bg-brand-100 hover:border-brand-400 transition-colors"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stage navigation button */}
               {!isLoading && !isCrisisState && detectedStage !== null && (
                <div className="px-4 py-3 bg-brand-50/30">
                  <button
                    onClick={() => handleStageClick(detectedStage)}
                    className="w-full text-center text-sm font-medium text-brand-700 bg-brand-100 hover:bg-brand-200 border border-brand-300 rounded-full py-2 transition-colors flex items-center justify-center gap-1.5"
                  >
                    {isZh
                      ? `查看 Stage ${detectedStage} 的所有疗愈师`
                      : `View All Stage ${detectedStage} Practitioners`}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        )}

        {/* Guided input section — only before first message */}
        {showGuidedInput && (
          <div className="px-4 pt-4 pb-2 space-y-3">
            {/* Step 1: Concern chips */}
            <div>
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                <Heart className="h-3 w-3" />
                {isZh ? '最近让你困扰的是？' : "What's been on your mind?"}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {concernChips.map((chip) => (
                  <button
                    key={chip.value}
                    onClick={() => handleConcernSelect(chip.value)}
                    className={cn(
                      'px-3 py-1.5 text-xs rounded-full border transition-all duration-200',
                      selectedConcern === chip.value
                        ? 'bg-brand-500 text-white border-brand-500 shadow-sm'
                        : 'bg-white text-foreground border-warm-200 hover:border-brand-300 hover:bg-brand-50'
                    )}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Duration chips — shown after concern selection */}
            {selectedConcern && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  {isZh ? '持续了多久？' : 'How long has this been going on?'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {durationChips.map((chip) => (
                    <button
                      key={chip.value}
                      onClick={() => handleDurationSelect(chip.value)}
                      className={cn(
                        'px-3 py-1.5 text-xs rounded-full border transition-all duration-200',
                        selectedDuration === chip.value
                          ? 'bg-brand-500 text-white border-brand-500 shadow-sm'
                          : 'bg-white text-foreground border-warm-200 hover:border-brand-300 hover:bg-brand-50'
                      )}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick send button when guided selections are made */}
            {canSendGuided && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300 flex items-center gap-2">
                <p className="text-xs text-muted-foreground flex-1">
                  {isZh 
                    ? '可以直接发送，或在下方补充更多细节' 
                    : 'Send now, or add more details below'}
                </p>
                <Button
                  onClick={handleQuickSend}
                  size="sm"
                  disabled={isLoading}
                  className="bg-brand-500 hover:bg-brand-600 text-white rounded-full text-xs px-4"
                >
                  {isZh ? '开始分析' : 'Analyze'}
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Input area */}
        <div className="p-4">
          {/* Show selected tags above input when guided */}
          {showGuidedInput && hasGuidedSelection && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selectedConcern && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-brand-100 text-brand-700">
                  {concernChips.find(c => c.value === selectedConcern)?.label}
                  <button onClick={() => { setSelectedConcern(null); setSelectedDuration(null); setGuidedStep('concern'); }} className="hover:text-brand-900">✕</button>
                </span>
              )}
              {selectedDuration && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full bg-brand-100 text-brand-700">
                  {durationChips.find(d => d.value === selectedDuration)?.label}
                  <button onClick={() => { setSelectedDuration(null); setGuidedStep('duration'); }} className="hover:text-brand-900">✕</button>
                </span>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={inputValue + (interimTranscript ? interimTranscript : '')}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                showGuidedInput
                  ? (isZh ? '可以补充更多细节，或直接选择上方选项...' : 'Add more details, or just pick options above...')
                  : (isZh ? '继续描述你的感受...' : 'Tell me more about how you feel...')
              }
              disabled={isLoading}
              className="flex-1 rounded-full border-warm-200"
            />
            {speechSupported && (
              <Button
                onClick={handleMicToggle}
                size="icon"
                variant={isRecording ? 'destructive' : 'ghost'}
                className={cn(
                  'rounded-full shrink-0 h-9 w-9',
                  isRecording ? 'animate-pulse' : 'text-muted-foreground/50 hover:text-muted-foreground'
                )}
                disabled={isLoading}
              >
                {isRecording ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              </Button>
            )}
            <Button
              onClick={handleSend}
              disabled={isLoading || (!inputValue.trim() && !canSendGuided)}
              size="icon"
              className="bg-brand-500 hover:bg-brand-600 text-white rounded-full shrink-0"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>

          {/* Follow-up suggestion chips - after AI responds */}
           {messages.length > 0 && !isLoading && !isCrisisState && quickReplyOptions.length === 0 && (
            <div className="mt-3">
              <p className="text-xs text-muted-foreground mb-2">{isZh ? '继续探索' : 'Keep exploring'}</p>
              <div className="flex flex-wrap gap-2">
                {followUpSuggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => sendMessage(s)}
                    className="px-3 py-1.5 text-xs rounded-full bg-brand-500 text-white hover:bg-brand-600 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Collapse toggle */}
          {messages.length > 0 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center gap-1 w-full mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {isExpanded
                ? (isZh ? '收起对话' : 'Collapse')
                : (isZh ? '展开对话' : 'Expand conversation')}
            </button>
          )}
        </div>

        <p className="text-[10px] text-muted-foreground text-center pb-3 px-4">
          {isZh
            ? '仅供参考，不构成医疗或专业建议'
            : 'For informational use only; not intended as medical, therapeutic, or professional advice.'}
        </p>
      </div>
    </div>
  );
};

export default WellnessConcierge;
